export type BookingStatus = 'pending' | 'confirmed' | 'cancelled';

export type SportType = 'futsal' | 'indoor_cricket' | 'indoor_cricket_nets' | 'badminton' | 'pickleball' | 'indoor_golf';

export type PaymentGateway = 'esewa' | 'khalti' | 'connectips' | 'fonepay' | 'bank' | 'cash' | 'qr';

export type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded';

export type PaymentSource = 'online' | 'manual';

export type BookingPaymentStatus =
  | 'unpaid'
  | 'pending'
  | 'partially_paid'
  | 'paid'
  | 'failed'
  | 'refunded';

export interface PaymentSummary {
  paymentStatus: BookingPaymentStatus;
  paidAmount: number;
  remainingAmount: number;
  latestPayment: Payment | null;
  canPay: boolean;
  canRecordManualPayment: boolean;
}

export interface Booking {
  id: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  sport: SportType;
  date: string;
  slot: string;
  amount: number;
  membershipDiscountPercent?: number;
  membershipId?: string | null;
  userPackageId?: string | null;
  coveredByPackage?: boolean;
  status: BookingStatus;
  notes?: string;
  createdByAdmin?: boolean;
  cancelledAt?: string | null;
  createdAt: string;
  cancelEligibility?: {
    canCancel: boolean;
    reason?: string;
  };
  paymentSummary?: PaymentSummary;
  paymentAccessToken?: string;
}

export type ScheduleSlotState = 'available' | 'booked' | 'closed' | 'past';
export type ScheduleView = 'day' | 'week';

export interface ScheduleSlotBooking {
  id: string;
  customerName: string;
  customerPhone: string;
  customerEmail?: string;
  status: Extract<BookingStatus, 'pending' | 'confirmed'>;
  amount: number;
  paymentStatus?: BookingPaymentStatus;
}

export interface ScheduleSlot {
  slot: string;
  state: ScheduleSlotState;
  booking: ScheduleSlotBooking | null;
}

export interface ScheduleCourtDay {
  sport: SportType;
  date: string;
  closed: boolean;
  enabled: boolean;
  slots: ScheduleSlot[];
}

export interface ScheduleGrid {
  from: string;
  to: string;
  dates: string[];
  sports: SportType[];
  days: ScheduleCourtDay[];
}

export interface Payment {
  id: string;
  bookingId: string;
  gateway: PaymentGateway;
  amount: number;
  status: PaymentStatus;
  source: PaymentSource;
  reference?: string;
  notes?: string;
  attachmentFileIds: string[];
  paidAt: string | null;
  recordedBy: string | null;
  transactionUuid?: string;
  createdAt: string;
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
}

export interface CustomerUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
}

export interface AdminCustomerUser {
  id: string;
  email: string;
  name: string;
  phone?: string;
  emailVerified: boolean;
  createdAt: string;
  activeMembership: UserMembership | null;
}

export interface AdminUserMembershipRow extends UserMembership {
  payments: {
    id: string;
    gateway: PaymentGateway;
    amount: number;
    status: PaymentStatus;
    source?: PaymentSource;
    reference?: string;
    notes?: string;
    paidAt: string | null;
    createdAt: string;
  }[];
}

export interface AdminUserPackageRow extends UserPackage {
  payments: {
    id: string;
    gateway: PaymentGateway;
    amount: number;
    status: PaymentStatus;
    source?: PaymentSource;
    reference?: string;
    notes?: string;
    paidAt: string | null;
    createdAt: string;
  }[];
}

export interface AdminCustomerDetail {
  user: AdminCustomerUser;
  memberships: AdminUserMembershipRow[];
  pendingMemberships: AdminUserMembershipRow[];
  packages: AdminUserPackageRow[];
  pendingPackages: AdminUserPackageRow[];
  activePackages: AdminUserPackageRow[];
}

export interface CourtSettings {
  id: string;
  sport: SportType;
  slotDurationMinutes: number;
  pricePerSlot: number;
  eveningPricePerSlot: number;
  eveningStartTime: string;
  openTime: string;
  closeTime: string;
  openDays: number[];
  enabled: boolean;
  discountPercent: number;
  discountValidTill: string | null;
  cancellationAllowed: boolean;
  cancellationCutoffMinutes: number | null;
  effectivePricePerSlot: number;
  effectiveEveningPricePerSlot: number;
  slots: string[];
  updatedAt: string;
}

export interface CourtSettingsInput {
  slotDurationMinutes: number;
  pricePerSlot: number;
  eveningPricePerSlot: number;
  eveningStartTime: string;
  openTime: string;
  closeTime: string;
  openDays: number[];
  enabled: boolean;
  discountPercent: number;
  discountValidTill: string | null;
  cancellationAllowed: boolean;
  cancellationCutoffMinutes: number | null;
}

export interface MembershipPlan {
  id: string;
  name: string;
  durationMonths: number;
  price: number;
  bookingDiscountPercent: number;
  description: string;
  benefits: string[];
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
}

export interface MembershipPlanInput {
  name: string;
  durationMonths: number;
  price: number;
  bookingDiscountPercent: number;
  description: string;
  benefits: string[];
  enabled: boolean;
  sortOrder: number;
}

export type UserMembershipStatus = 'pending' | 'active' | 'expired' | 'cancelled';

export type MembershipPaymentStatus = 'unpaid' | 'paid' | 'failed' | 'refunded';

export interface MembershipPaymentSummary {
  paymentStatus: MembershipPaymentStatus;
  latestPayment: {
    id: string;
    membershipId: string;
    gateway: PaymentGateway;
    amount: number;
    status: PaymentStatus;
    paidAt: string | null;
    transactionUuid?: string;
    createdAt: string;
  } | null;
  canPay: boolean;
}

export interface UserMembership {
  id: string;
  planId: string;
  planName: string;
  durationMonths: number;
  planPrice: number;
  bookingDiscountPercent: number;
  startDate: string;
  expiresAt: string;
  status: UserMembershipStatus;
  isValid: boolean;
  createdAt: string;
  paymentSummary?: MembershipPaymentSummary;
}

export interface BookingPricing {
  basePrice: number;
  courtEffectivePrice: number;
  courtDiscountPercent: number;
  membershipDiscountPercent: number;
  finalAmount: number;
  membershipId: string | null;
  hasActiveMembership: boolean;
  userPackageId: string | null;
  coveredByPackage: boolean;
  packageTitle: string | null;
  packageSlotsRemaining: number | null;
  packageTimeFrameStart: string | null;
  packageTimeFrameEnd: string | null;
  packageOutsideTimeFrame: boolean;
  slotPeriod: 'day' | 'evening';
}

export type PackageDurationUnit = 'days' | 'months' | 'years';

export interface PackagePlan {
  id: string;
  title: string;
  description: string;
  sport: SportType;
  price: number;
  durationUnit: PackageDurationUnit;
  durationValue: number;
  totalSlots: number;
  slotTimeStart: string;
  slotTimeEnd: string;
  enabled: boolean;
  sortOrder: number;
  updatedAt: string;
}

export interface PackagePlanInput {
  title: string;
  description: string;
  sport: SportType;
  price: number;
  durationUnit: PackageDurationUnit;
  durationValue: number;
  totalSlots: number;
  slotTimeStart: string;
  slotTimeEnd: string;
  enabled: boolean;
  sortOrder: number;
}

export type UserPackageStatus = 'pending' | 'active' | 'expired' | 'cancelled';

export type PackagePaymentStatus = 'unpaid' | 'paid' | 'failed' | 'refunded';

export interface PackagePaymentSummary {
  paymentStatus: PackagePaymentStatus;
  latestPayment: {
    id: string;
    userPackageId: string;
    gateway: PaymentGateway;
    amount: number;
    status: PaymentStatus;
    paidAt: string | null;
    transactionUuid?: string;
    createdAt: string;
  } | null;
  canPay: boolean;
}

export interface UserPackage {
  id: string;
  packageId: string;
  title: string;
  sport: SportType;
  packagePrice: number;
  durationUnit: PackageDurationUnit;
  durationValue: number;
  totalSlots: number;
  slotsUsed: number;
  slotsRemaining: number;
  slotTimeStart: string | null;
  slotTimeEnd: string | null;
  startDate: string;
  expiresAt: string;
  status: UserPackageStatus;
  isValid: boolean;
  createdAt: string;
  paymentSummary?: PackagePaymentSummary;
}

export type AdminNotificationType =
  | 'booking_created'
  | 'booking_confirmed'
  | 'booking_cancelled'
  | 'payment_received'
  | 'package_purchased';

export interface AdminNotification {
  id: string;
  type: AdminNotificationType;
  title: string;
  message: string;
  link: string;
  bookingId?: string;
  paymentId?: string;
  userPackageId?: string;
  read: boolean;
  createdAt: string;
}

export interface DashboardAnalytics {
  totalBookings: number;
  pendingBookings: number;
  confirmedBookings: number;
  bookingRevenue: number;
  packageRevenue: number;
  membershipRevenue: number;
  totalRevenue: number;
  totalDueAmount: number;
  bookingsBySport: { sport: SportType; count: number }[];
  revenueByMonth: { month: string; revenue: number }[];
  paymentsByGateway: { gateway: PaymentGateway; count: number }[];
}

export const DAY_LABELS: { value: number; label: string }[] = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

export interface VenueShowcaseItem {
  id: string;
  src: string;
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId: string | null;
  staticSrc: string | null;
  updatedAt: string;
}

export type GalleryEntryType = 'image' | 'highlight';
export type GalleryHighlightIcon = 'lighting' | 'surface' | 'climate' | 'sports';

export interface GalleryImageItem {
  id: string;
  entryType: 'image';
  src: string;
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId: string | null;
  staticSrc: string | null;
  updatedAt: string;
}

export interface GalleryHighlightItem {
  id: string;
  entryType: 'highlight';
  title: string;
  description: string;
  iconKey: GalleryHighlightIcon;
  accentColor: string | null;
  sortOrder: number;
  enabled: boolean;
  updatedAt: string;
}

export type GalleryEntry = GalleryImageItem | GalleryHighlightItem;

export interface GalleryImageInput {
  entryType: 'image';
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId?: string | null;
  staticSrc?: string | null;
}

export interface GalleryHighlightInput {
  entryType: 'highlight';
  title: string;
  description: string;
  iconKey: GalleryHighlightIcon;
  accentColor?: string;
  sortOrder: number;
  enabled: boolean;
}

export type GalleryEntryInput = GalleryImageInput | GalleryHighlightInput;

export interface VenueShowcaseInput {
  alt: string;
  caption: string;
  sport: string;
  sortOrder: number;
  enabled: boolean;
  imageFileId?: string | null;
  staticSrc?: string | null;
}

export interface Blog {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  coverImageUrl: string | null;
  coverImageFileId: string | null;
  contentImageFileIds: string[];
  enabled: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BlogSummary {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  coverImageUrl: string | null;
  publishedAt: string;
  updatedAt: string;
}

export interface BlogInput {
  title: string;
  slug?: string;
  excerpt: string;
  content: string;
  coverImageFileId?: string | null;
  enabled: boolean;
}

export const SPORT_LABELS: Record<SportType, string> = {
  futsal: 'Futsal',
  indoor_cricket: 'Indoor Cricket',
  indoor_cricket_nets: 'Indoor Cricket Nets',
  badminton: 'Badminton',
  pickleball: 'Pickle Ball',
  indoor_golf: 'Indoor Golf',
};
